//@ts-check
import {curryN, pathOr, mergeDeepRight} from 'ramda';
import EXIF from 'exif-js';
import {fetchV1} from './fetch';

const postForm = (url, formData) => fetchV1('*/*')(url, {
    method: 'POST',
    credentials: 'include',
    body: formData,
});

const processExif = ({file, filedata = {}, resolve}) => function() {
    // 'this' is the image
    const iptcdata = this.iptcdata || {};
    const exifdata = this.exifdata || {};

    // This field is reliant on a modification that we've made directly to exif-js
    // If this stops working, please ensure 0x05 : 'title', is in the IptcFieldMap in exif.js
    const title = (iptcdata.hasOwnProperty('title') ? iptcdata.title : false) || file.name;

    let caption = filedata.caption || title;
    if (iptcdata.hasOwnProperty('caption')) {
        caption = iptcdata.caption;
    } else if (exifdata.hasOwnProperty('ImageDescription')) {
        caption = exifdata.ImageDescription;
    }

    resolve({
        ...filedata,
        caption,
        isGif: file.mimeType === 'image/gif',
        metadata: JSON.stringify(mergeDeepRight(exifdata, iptcdata)),
        mimeType: file.mimeType,
        renditions: {},
        title,
    });
};

export const getFileData = file => new Promise((resolve, reject) => {
    if (!file) {
        return reject(new Error('File not specified'));
    }

    if (!file.type && typeof file.type !== 'string') {
        return reject(new Error('File type invalid'));
    }

    const filedata = {};
    filedata.filename = file.name;

    if (file.type.match('image')) {
        filedata.mediaType = 'image';
        return EXIF.getData(file, processExif({file, filedata, resolve}));
    } else if (file.type.match(/video|audio/i)) {
        filedata.mediaType = 'video';
        filedata.title = filedata.caption = filedata.alt = file.name.replace(/\.\w+$/, '');
    }
    return resolve(filedata);
});

export const uploadFileWithData = curryN(3, (url, file, filedata) => {
    const uploadForm = new FormData();

    // Append files to the upload form
    uploadForm.append('file', file);

    // Add filedata to the upload form
    uploadForm.append(filedata.filename, JSON.stringify(filedata));

    return postForm(url, uploadForm)
        .then((data) => {
            if (!data) {
                console.error('Upload error: Server returned an empty response.');  //eslint-disable-line no-console
                throw new Error('There was an error uploading the file.');
            }

            return data;
        }).catch((err) => {
            const message = pathOr(
                `Upload error: ${err.message}`,
                ['data', '0', 'msg'],
                err
            );

            console.error(message); //eslint-disable-line no-console
            throw new Error('There was an error uploading the file.');
        });
});

export const uploadFile = curryN(2, (url, file) =>
    getFileData(file).then(uploadFileWithData(url, file))
);

// pullFilesFromInput :: HTMLInputElement? -> [File]
export const pullFilesFromInput = input => Array.from((input && input.files) || []);

export const uploadFilesFromInput = (input, {url = `/platform/media`} = {}) =>
    Promise.all(
        pullFilesFromInput(input).map(
            uploadFile(url)
        )
    )
;

export default {
    getFileData,
    pullFilesFromInput,
    uploadFileWithData,
    uploadFile,
    uploadFilesFromInput,
};
